<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { UiModalHeader, IconImage } from '@/shared/ui';
  import { useFormBuilderToast } from '../../../../composables/useFormBuilderToast';
  import ModalActions from '@/shared/ui/modals/ModalActions.vue';
  import TrashIcon from '@/shared/assets/icons/RedTrashIcon.svg';
  import { formBuilderCompactSuccessToastStyle } from '../../../../composables/showFormElementDeleteToast';

  type DeleteContext = 'row' | 'column' | 'table' | 'selection';

  interface Props {
    context: DeleteContext;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'confirm'): void;
  }>();

  const { showSuccessToast } = useFormBuilderToast();

  const contextLabels: Record<DeleteContext, string> = {
    row: 'linha',
    column: 'coluna',
    table: 'tabela',
    selection: 'seleção',
  };

  const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

  const message = computed(() => {
    const label = contextLabels[props.context];

    return {
      title: `Tem certeza que deseja deletar a ${label}?`,
      description: ' A ação não pode ser desfeita e as edições feitas serão perdidas.',
      successMessage: `${capitalize(label)} deletada com sucesso.`,
    };
  });

  const handleClose = () => {
    emit('close');
  };

  const handleConfirm = () => {
    emit('confirm');
    showSuccessToast(
      {
        title: message.value.successMessage,
        type: 'success',
      },
      formBuilderCompactSuccessToastStyle
    );
  };
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl max-w-md w-full mx-4"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <UiModalHeader class="px-6 py-4 text-sm" @close="handleClose" />
    <div class="flex flex-col px-14 pb-10">
      <IconImage :src="TrashIcon" alt="Deletar" class="w-6 h-6 mb-7 mx-auto" />
      <p class="text-center text-base">
        {{ message.title }}
      </p>
      <p class="text-center text-base text-gray-03">
        {{ message.description }}
      </p>
      <ModalActions class="mt-8" @close="handleClose" @confirm="handleConfirm" />
    </div>
  </VueFinalModal>
</template>
